import { useState, useEffect } from 'react';
import { ShieldCheck, X, LoaderCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Telemetry } from '@/lib/telemetry';
import { usePermissions } from '@/lib/usePermissions';

const DECLARATION_FALLBACK = 'I acknowledge this readiness declaration.';
const DECLARATION_TIMEOUT_MS = 5000;

interface ReadinessDialogProps {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReadinessDialog({ projectId, onClose, onSuccess }: ReadinessDialogProps) {
  const { canDeclareReadiness } = usePermissions();
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [declarationText, setDeclarationText] = useState<string | null>(null);
  const [declarationLoading, setDeclarationLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      if (!cancelled) {
        setDeclarationText(DECLARATION_FALLBACK);
        setDeclarationLoading(false);
      }
    }, DECLARATION_TIMEOUT_MS);

    api.readiness
      .declarationText(projectId)
      .then((res) => {
        if (!cancelled) {
          setDeclarationText(res.declarationText);
          setDeclarationLoading(false);
          clearTimeout(timer);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDeclarationText(DECLARATION_FALLBACK);
          setDeclarationLoading(false);
          clearTimeout(timer);
        }
      });

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [projectId]);

  const handleDeclare = async () => {
    setSubmitting(true);
    setError('');
    try {
      const result = await api.readiness.declare(projectId, {}, undefined, true);
      Telemetry.readinessDeclared({ snapshot_hash: result?.snapshotHash ?? '', finding_count: 0 });
      onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to declare readiness');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-brand-orange" />
            <h2 className="text-lg font-bold text-brand-charcoal">Declare Readiness</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-slate-600">
            This action creates an immutable snapshot of the current project state,
            including all findings, dismissals, sign-offs, document hashes, and prompt versions.
          </p>

          {/* Declaration text */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">Declaration</p>
            {declarationLoading ? (
              <div className="h-4 bg-amber-200/60 rounded animate-pulse w-3/4" />
            ) : (
              <p className="text-sm text-amber-800">{declarationText}</p>
            )}
          </div>

          {/* Acknowledgment */}
          <label className="flex items-start gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 rounded border-slate-300 text-brand-orange focus:ring-brand-orange/30"
            />
            <span className="text-sm text-slate-600">
              I acknowledge the above declaration and confirm this readiness snapshot.
            </span>
          </label>

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDeclare}
            disabled={!acknowledged || submitting || !canDeclareReadiness || declarationLoading}
            title={!canDeclareReadiness ? 'Requires Lead role' : undefined}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-orange rounded-lg hover:bg-orange-600 disabled:opacity-50${!canDeclareReadiness ? ' cursor-not-allowed' : ''}`}
          >
            {submitting && <LoaderCircle className="w-4 h-4 animate-spin" />}
            Declare Readiness
          </button>
        </div>
      </div>
    </div>
  );
}
